import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { Dialog } from '../../ui/Dialog';
import { ScalePicker } from '../../components/ScalePicker';
import { MOOD_SCALE } from '../../lib/constants';
import { errorText, fmtDate, rpc, type AppError } from '../admin/api';
import { StaffFrame } from '../r3/common';

// I5/I6: the staff member's own check-in. Private: only they ever see their
// answers. With consent, the first check-in each month also joins the
// school's anonymous staff picture (no name, no time). Withdrawing deletes
// the history at once.

type Dim = 'workload' | 'energy' | 'sleep' | 'stress';
const DIMS: Dim[] = ['workload', 'energy', 'sleep', 'stress'];
type Row = Record<Dim, number> & { day: string };
type Home = { consented: boolean; consent_version: number; today: Record<Dim, number> | null; counted_this_month: boolean; history: Row[] };

export function MyCheckinPage() {
  const { t } = useI18n();
  return (
    <StaffFrame roles={['teacher', 'head_of_school', 'counsellor', 'school_admin']} title={t['r3.me.title']}
      extraNav={[{ icon: '🫶', label: t['r3.me.nav'], href: '/me/checkin', active: true }]}>
      {() => <Body />}
    </StaffFrame>
  );
}

function Body() {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [h, setH] = useState<Home | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [vals, setVals] = useState<Partial<Record<Dim, number>>>({});
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [agree, setAgree] = useState(false);

  const load = useCallback(async () => {
    const r = await rpc<Home>('staff_checkin_home');
    if (r.error) return setErr(r.error);
    setH(r.data);
    if (r.data.today) setVals(r.data.today);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function consent(give: boolean) {
    setErr(null);
    const r = await rpc<Home>('staff_checkin_consent', { p_give: give });
    if (r.error) return setErr(r.error);
    setH(r.data);
    setConfirmWithdraw(false);
    if (!give) setVals({});
    announce(give ? t['r3.me.consentGiven'] : t['r3.me.withdrawn']);
  }
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    setErr(null);
    const r = await rpc<Home>('staff_checkin_submit', { p_workload: vals.workload, p_energy: vals.energy, p_sleep: vals.sleep, p_stress: vals.stress });
    setBusy(false);
    if (r.error) return setErr(r.error);
    setH(r.data);
    announce(t['r3.me.saved']);
  }

  if (!h) return err ? <p className="notice notice-block" role="alert">{errorText(t, err)}</p> : <p role="status">{t.loading}</p>;

  if (!h.consented) {
    return (
      <section className="panel p5" aria-labelledby={`${id}-c`}>
        <h2 className="panel-title" id={`${id}-c`}>{t['r3.me.consentTitle']} <Pill kind="warn">{t['ts.draft']}</Pill></h2>
        <ul className="plain-list">
          <li>{t['r3.me.c1']}</li>
          <li>{t['r3.me.c2']}</li>
          <li>{t['r3.me.c3']}</li>
          <li>{t['r3.me.c4']}</li>
        </ul>
        <label className="check">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          <span>{t['r3.me.agree']}</span>
        </label>
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <div className="form-actions">
          <button type="button" className="btn btn-primary" disabled={!agree} onClick={() => consent(true)}>{t['r3.me.consent']}</button>
        </div>
      </section>
    );
  }

  return (
    <>
      <p className="notice notice-block">{t['r3.me.private']} {h.counted_this_month ? t['r3.me.counted'] : t['r3.me.notCounted']}</p>
      <MyCheckinDeck
        vals={vals}
        setVal={(d, v) => setVals({ ...vals, [d]: v })}
        intro={h.today ? t['r3.me.todayDone'] : t['r3.me.todayTitle']}
        onSubmit={submit}
        submitting={busy}
        error={err && errorText(t, err)}
      />
      <section className="panel p5" aria-labelledby={`${id}-h`}>
        <h2 className="panel-title" id={`${id}-h`}>{t['r3.me.history']}</h2>
        {h.history.length === 0 ? (
          <p className="text-muted">{t['r3.me.noHistory']}</p>
        ) : (
          <div className="table-wrap" tabIndex={0} role="region" aria-labelledby={`${id}-h`}>
            <table className="table">
              <caption className="visually-hidden">{t['r3.me.history']}</caption>
              <thead>
                <tr><th scope="col">{t['r3.me.day']}</th>{DIMS.map((d) => <th key={d} scope="col">{t[`r3.dim.${d}` as 'r3.dim.workload']}</th>)}</tr>
              </thead>
              <tbody>
                {h.history.map((r) => (
                  <tr key={r.day}><th scope="row">{fmtDate(locale, r.day)}</th>{DIMS.map((d) => <td key={d}>{r[d]}/5</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="form-hint">{t['r3.me.keep']}</p>
        <button type="button" className="btn btn-ghost" onClick={() => setConfirmWithdraw(true)}>{t['r3.me.withdraw']}</button>
      </section>
      <Dialog open={confirmWithdraw} title={t['r3.me.withdrawTitle']} intro={<p>{t['r3.me.withdrawBody']}</p>} submitLabel={t['r3.me.withdrawConfirm']}
        onSubmit={() => consent(false)} onClose={() => setConfirmWithdraw(false)} />
    </>
  );
}

type Step = Dim | 'review';
const STEPS: Step[] = [...DIMS, 'review'];

// §8.4: the four 1–5 scales each as their own face card (same stepper
// pattern as the teacher's Reflect deck and the admin "Add a school"
// wizard — numbered circles, a step heading that takes focus on change,
// Back/Next), then a review card and Save. Nothing pre-selected; changing
// an earlier answer is a Back click away, same as every other deck here.
function MyCheckinDeck({ vals, setVal, intro, onSubmit, submitting, error }: {
  vals: Partial<Record<Dim, number>>;
  setVal: (d: Dim, v: number) => void;
  intro: string;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const { t } = useI18n();
  const [step, setStep] = useState<Step>(DIMS[0]);
  const idx = STEPS.indexOf(step);
  const stepHeading = useRef<HTMLHeadingElement>(null);
  const firstStep = useRef(true);
  const id = useId();

  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    stepHeading.current?.focus();
  }, [step]);

  const stepTitle: Record<Step, string> = {
    workload: t['r3.dim.workload'], energy: t['r3.dim.energy'], sleep: t['r3.dim.sleep'], stress: t['r3.dim.stress'],
    review: t['pa.wizReview'],
  };

  return (
    <>
      <section className="panel p5" aria-labelledby={`${id}-step`}>
        <ol className="stepper" aria-label={t['pa.wizSteps']}>
          {STEPS.map((s, i) => (
            <li key={s} className={i === idx ? 'on' : i < idx ? 'done' : ''} aria-current={i === idx ? 'step' : undefined}>
              <span className="stepper-n" aria-hidden="true">{i < idx ? '✓' : i + 1}</span>
              <span>{stepTitle[s]}</span>
              {i < idx && <span className="visually-hidden"> ({t['pa.done']})</span>}
            </li>
          ))}
        </ol>
        <h2 className="panel-title" id={`${id}-step`} ref={stepHeading} tabIndex={-1}>
          {t['pa.wizStepOf'](idx + 1, STEPS.length, stepTitle[step])}
        </h2>
        {idx === 0 && <p className="form-hint">{intro}</p>}

        {step !== 'review' ? (
          <>
            <p className="form-hint" id={`${id}-hint`}>{t[`r3.dimHint.${step}` as 'r3.dimHint.workload']}</p>
            <ScalePicker value={vals[step] ?? null} onChange={(v) => setVal(step, v)} labelledBy={`${id}-step`} describedBy={`${id}-hint`} />
          </>
        ) : (
          <dl className="kv">
            {DIMS.map((d) => (
              <div key={d}>
                <dt>{t[`r3.dim.${d}` as 'r3.dim.workload']}</dt>
                <dd>
                  <span aria-hidden="true">{MOOD_SCALE.find((m) => m.value === vals[d])?.emoji} </span>
                  {t['r3.me.pickedOf'](vals[d] ?? 0)}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {error && <p className="error-text" role="alert">{error}</p>}
        <div className="form-actions">
          {idx > 0 && (
            <button type="button" className="btn btn-ghost" onClick={() => setStep(STEPS[idx - 1])} disabled={submitting}>
              {t['pa.back']}
            </button>
          )}
          {step !== 'review' ? (
            <button type="button" className="btn btn-primary" disabled={!vals[step]} onClick={() => setStep(STEPS[idx + 1])}>
              {t['pa.next']}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={onSubmit}>
              {submitting ? t['pa.working'] : t['r3.me.save']}
            </button>
          )}
        </div>
      </section>
    </>
  );
}
